import React, { ReactNode, createContext, useContext, useMemo, useState, useEffect, useRef } from 'react';
import { LoadScript } from '@react-google-maps/api';
import { useAppSettings } from '../../context/AppSettingsContext';
import { useTranslation } from 'react-i18next';

const libraries: ("places" | "routes" | "marker")[] = ['places', 'routes', 'marker'];

interface GoogleMapsContextType {
  isLoaded: boolean;
  loadError: boolean;
}

const GoogleMapsContext = createContext<GoogleMapsContextType>({
  isLoaded: false,
  loadError: false,
});

export const useGoogleMapsContext = () => useContext(GoogleMapsContext);

interface ConditionalGoogleMapsProviderProps {
  children: ReactNode;
}

function isValidApiKey(key: string | undefined): boolean {
  if (!key || key.trim() === '') return false;
  if (key === 'your_google_maps_api_key_here') return false;
  if (key.includes('REPLACE')) return false;
  if (key.includes('your_')) return false;
  if (key.length < 20) return false;
  return true;
}

export function ConditionalGoogleMapsProvider({ children }: ConditionalGoogleMapsProviderProps) {
  const { isEnabled } = useAppSettings();
  const { i18n } = useTranslation();
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  // Map i18n language codes to Google Maps language codes.
  // Note: Google Maps language is set at load time.
  // Language changes require page reload to take effect on Google Maps.
  const googleLang = i18n.language.startsWith('zh-CN') ? 'zh-CN'
    : i18n.language.startsWith('zh-TW') ? 'zh-TW'
    : i18n.language === 'ja' ? 'ja'
    : i18n.language === 'ko' ? 'ko'
    : i18n.language === 'it' ? 'it'
    : 'en';
  const mapsEnabled = isEnabled('api_google_maps');
  const shouldLoad = mapsEnabled && isValidApiKey(apiKey);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const authFailedRef = useRef(false);

  useEffect(() => {
    if (!shouldLoad) return;

    (window as any).gm_authFailure = () => {
      console.warn('Google Maps authentication failed — API key invalid or restricted');
      authFailedRef.current = true;
      setLoadError(true);
      setIsLoaded(false);
    };

    return () => {
      delete (window as any).gm_authFailure;
    };
  }, [shouldLoad]);

  const disabledValue = useMemo(() => ({ isLoaded: false, loadError: true }), []);
  const loadedValue = useMemo(() => ({ isLoaded, loadError }), [isLoaded, loadError]);

  if (!shouldLoad) {
    return (
      <GoogleMapsContext.Provider value={disabledValue}>
        {children}
      </GoogleMapsContext.Provider>
    );
  }

  return (
    <LoadScript
      googleMapsApiKey={apiKey}
      libraries={libraries}
      language={googleLang}
      onLoad={() => {
        if (!authFailedRef.current) setIsLoaded(true);
      }}
      onError={(error) => {
        console.error('Google Maps failed to load:', error);
        setLoadError(true);
        setIsLoaded(false);
      }}
    >
      <GoogleMapsContext.Provider value={loadedValue}>
        {children}
      </GoogleMapsContext.Provider>
    </LoadScript>
  );
}
